/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  A geos call to determine our location after center changes on the map
 */

import { locate } from '@zcode/nr/es/shared/api/geos'
import { getBestCity, getBestHood } from '@zcode/nr/es/shared/utils/location'
import type { MainMapMetaTypes } from '@zcode/state-type'
import type { selectMapCenter } from '../map/selectors/index'

const hoodsLimit = 12
const citiesLimit = 12

// TODO: move this to a place where others might use it.
const locateCenter = (center: ReturnType<typeof selectMapCenter>, box: MainMapMetaTypes['box']) =>
  locate({ lat: center.lat, lng: center.lng, hoodsLimit, citiesLimit }).then((geos) => {
    // NOTE: The Geos.locate method chomps the lat/lng. Here we're grabbing the hood that is closest
    // to the center instead of relying on geos.hood. Notice that the getBestHood function will
    // return undefined if the center is not actually within any of the hoods.
    const geosHood =
      geos.hood || geos.nearbyHoods
        ? getBestHood({
            center,
            hood: geos.hood,
            nearbyHoods: geos.nearbyHoods,
            useGeos: true,
          })
        : undefined

    // NOTE: The Geos.locate method chomps the lat/lng. This means that the first city in the
    // geos.cities array is not necessarily the city we're looking for. Here we take the city
    // closest to the center.
    const geosCity = getBestCity(center, box, geos)

    // NOTE: we're returning only the important parts of the geos call. We use this to update the
    // browser URL and to update the currentLocation.
    return {
      city: geosCity,
      hood: geosHood,
    }
  })

export default locateCenter
